import React, {useEffect, useMemo, useState} from 'react';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {toast} from '@ui/toast/toast';
import {User} from '@ui/types/user';
import {CssTheme} from '@ui/themes/css-theme';
import {mergeBootstrapData, useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {setCookie} from '@ui/utils/hooks/use-cookie';

interface ThemeInfo extends CssTheme {
  allowed_price_labels?: string[];
}

interface Props {
  user: User;
}

export function ThemePanel({user}: Props) {
  const currentData = useBootstrapDataStore(s => s.data);
  const [themes, setThemes] = useState<ThemeInfo[]>([]);
  const [selectedThemeId, setSelectedThemeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let aborted = false;
    async function load() {
      try {
        const res = await fetch('/api/v1/users/me/theme', {
          credentials: 'include',
          headers: {Accept: 'application/json'},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        setThemes((data?.themes || []) as ThemeInfo[]);
        setSelectedThemeId(data?.selectedThemeId ?? null);
      } catch (e) {
        // ignore
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, []);

  const onApply = async (themeId: number | null) => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/users/me/theme', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({theme_id: themeId}),
      });
      if (res.ok) {
        setSelectedThemeId(themeId);
        const mergedThemes = mergeThemes(currentData.themes.all, themes);
        const nextSelectedThemeId =
          themeId ??
          mergedThemes.find(theme => theme.default_light)?.id ??
          mergedThemes[0]?.id ??
          null;

        mergeBootstrapData({
          user: currentData.user
            ? {...currentData.user, theme_id: themeId}
            : currentData.user,
          themes: {
            ...currentData.themes,
            all: mergedThemes,
            selectedThemeId: nextSelectedThemeId,
          },
        });

        setCookie('be-active-theme', themeId ? `${themeId}` : '');
        toast(<Trans message="Tema aplicado com sucesso." />);
        // Disparar recarregamento imediato do CSS do tema
        try {
          window.dispatchEvent(new Event('user-theme:reload'));
        } catch {}
      } else if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        toast(data?.message || 'Este tema não está disponível no seu plano.');
      } else {
        toast('Não foi possível aplicar o tema.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccountSettingsPanel id="theme" title={<Trans message="Aparência / Tema" />}>
      <div className="max-w-720 space-y-20">
        <p className="text-muted">
          <Trans message="Escolha seu tema entre as opções disponíveis para o seu plano." />
        </p>
        <div className="grid grid-cols-1 gap-16 md:grid-cols-2 lg:grid-cols-3">
          {themes.map(theme => (
            <div key={theme.id} className="rounded border p-16">
              <div className="mb-10 text-sm font-medium">
                {theme.name || `Tema #${theme.id}`} {theme.is_dark ? '(Escuro)' : '(Claro)'}
              </div>
              {Array.isArray(theme.allowed_price_labels) && theme.allowed_price_labels.length > 0 && (
                <div className="mb-12 flex flex-wrap gap-8">
                  {theme.allowed_price_labels.map((label, idx) => (
                    <span key={idx} className="rounded-full bg-chip px-8 py-4 text-xs text-main">
                      {label}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-8">
                <Button
                  size="xs"
                  variant={selectedThemeId === theme.id ? 'flat' : 'raised'}
                  disabled={loading}
                  onClick={() => onApply(theme.id)}
                >
                  {selectedThemeId === theme.id ? <Trans message="Selecionado" /> : <Trans message="Selecionar" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Button size="xs" variant="outline" disabled={loading} onClick={() => onApply(null)}>
            <Trans message="Usar padrão do site (Claro/Escuro)" />
          </Button>
        </div>
      </div>
    </AccountSettingsPanel>
  );
}

function mergeThemes(currentThemes: CssTheme[], fetchedThemes: ThemeInfo[]) {
  const map = new Map<number | string, CssTheme>();

  currentThemes.forEach(theme => {
    map.set(theme.id, theme);
  });

  fetchedThemes.forEach(theme => {
    map.set(theme.id, {...map.get(theme.id), ...theme});
  });

  return Array.from(map.values());
}
